import React from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Inbox, ArrowRight } from "lucide-react";
import { Button } from "./Button";
import { initials } from "../format";

/** Antet de pagină: titlu (serif), descriere, acțiuni la dreapta. Breadcrumb-ul e afișat în topbar (PortalShell). */
export function PageHeader({ title, description, actions, meta, className = "", titleTestId = "page-title", size = "lg" }) {
  return (
    <header className={`mb-6 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          {meta && <div className="mb-2">{meta}</div>}
          <h1 className={size === "lg" ? "cp-display" : "cp-h1"} data-testid={titleTestId}>{title}</h1>
          {description && <p className="cp-body-lg mt-1.5 max-w-3xl">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0 sm:pt-2">{actions}</div>}
      </div>
    </header>
  );
}

/** Secțiune: titlu mic (sans semibold) + acțiune opțională. */
export function Section({ title, description, action, children, className = "", id, serif = false }) {
  return (
    <section id={id} className={className} aria-labelledby={id ? `${id}-title` : undefined}>
      {(title || action) && (
        <div className="flex items-start justify-between gap-4 mb-3.5">
          <div>
            {title && <h2 id={id ? `${id}-title` : undefined} className={serif ? "cp-h2" : "cp-h3"}>{title}</h2>}
            {description && <p className="cp-meta mt-0.5">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Panel({ children, className = "", padded = true, ...rest }) {
  return <div className={`cp-panel ${padded ? "p-5" : ""} ${className}`} {...rest}>{children}</div>;
}

/** Card cu titlu serif (ex. „Rezumat portofoliu”, „Starea proiectului în portofoliu”) */
export function TitledPanel({ title, action, children, className = "", testid }) {
  return (
    <div className={`cp-panel p-5 ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          {title && <h3 className="cp-h2 !text-[19px]">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/** Pătrat cu icon (KPI / listă) */
export function IconTile({ icon: Icon, tone = "", size = "", className = "" }) {
  return <span className={`cp-tile ${tone ? `is-${tone}` : ""} ${size ? `is-${size}` : ""} ${className}`} aria-hidden="true"><Icon /></span>;
}

/** Card KPI: icon | etichetă + valoare serif */
export function KpiCard({ icon, label, value, sub, tone, testid, className = "", to }) {
  const body = (
    <>
      <IconTile icon={icon} tone={tone} />
      <div className="min-w-0 pl-4 border-l border-cp-border">
        <div className="text-[12.5px] text-cp-text-2 leading-tight">{label}</div>
        <div className="cp-metric mt-1 truncate">{value}</div>
        {sub && <div className="cp-meta mt-1 truncate">{sub}</div>}
      </div>
    </>
  );
  const cls = `cp-panel flex items-center gap-4 px-4 py-4 min-w-0 ${to ? "cp-card-hover" : ""} ${className}`;
  return to ? <Link to={to} className={cls} data-testid={testid}>{body}</Link> : <div className={cls} data-testid={testid}>{body}</div>;
}

/** Fapt mic: etichetă + valoare bold (folosit în benzi de statistici) */
export function Fact({ label, value, icon: Icon, className = "", valueClass = "" }) {
  return (
    <div className={`flex items-center gap-3 min-w-0 ${className}`}>
      {Icon && <Icon className="h-5 w-5 text-cp-text-2 shrink-0" aria-hidden="true" />}
      <div className="min-w-0">
        <div className="cp-meta">{label}</div>
        <div className={`text-[15px] font-semibold text-cp-text leading-snug truncate ${valueClass}`}>{value}</div>
      </div>
    </div>
  );
}

/** Link „Vezi detalii →” */
export function ArrowLink({ to, onClick, children, className = "", testid, href }) {
  const cls = `inline-flex items-center gap-1.5 text-[13px] font-semibold text-cp-accent hover:underline underline-offset-4 ${className}`;
  const inner = <>{children} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></>;
  if (href) return <a href={href} className={cls} data-testid={testid}>{inner}</a>;
  if (to) return <Link to={to} className={cls} data-testid={testid}>{inner}</Link>;
  return <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
}

export function EmptyState({ icon: Icon = Inbox, title, description, action, compact = false, testid = "empty-state", className = "" }) {
  return (
    <div data-testid={testid} className={`text-center cp-panel ${compact ? "py-8 px-4" : "py-14 px-6"} ${className}`}>
      <div className={`mx-auto rounded-full bg-cp-accent-soft grid place-items-center text-cp-accent mb-4 ${compact ? "h-12 w-12" : "h-20 w-20"}`}>
        <Icon className={compact ? "h-5 w-5" : "h-8 w-8"} aria-hidden="true" />
      </div>
      <p className={compact ? "cp-h4" : "cp-h2"}>{title}</p>
      {description && <p className="cp-body mt-1.5 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ title = "Nu am putut încărca datele", description = "Verifică conexiunea și încearcă din nou.", onRetry }) {
  return (
    <div role="alert" className="cp-panel p-6 text-center border-cp-danger-border bg-cp-danger-soft/40">
      <p className="cp-h4 text-cp-danger">{title}</p>
      <p className="cp-body-sm mt-1">{description}</p>
      {onRetry && <Button variant="secondary" size="sm" className="mt-4" onClick={onRetry}><RefreshCw /> Reîncearcă</Button>}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`cp-skeleton ${className}`} aria-hidden="true" />;
}

export function SkeletonRows({ rows = 3, height = "h-14" }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className={`${height} w-full`} />)}
    </div>
  );
}

export function Avatar({ name, src, size = "md", className = "", dark = false }) {
  const sz = { xs: "h-7 w-7 text-[10px]", sm: "h-8 w-8 text-[11px]", md: "h-10 w-10 text-[13px]", lg: "h-12 w-12 text-[15px]", xl: "h-16 w-16 text-[20px]", "2xl": "h-24 w-24 text-[30px]" }[size];
  return src ? (
    <img src={src} alt={name} className={`${sz} rounded-full object-cover shrink-0 ${className}`} />
  ) : (
    <span className={`${sz} rounded-full font-semibold grid place-items-center shrink-0 ${dark ? "bg-white/15 text-white" : "bg-cp-navy text-white"} ${className}`} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

export function Progress({ value = 0, tone, className = "", label }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const cls = tone ? `is-${tone}` : "";
  return (
    <div className={`cp-progress ${cls} ${className}`} role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100} aria-label={label || "Progres"}>
      <span style={{ width: `${v}%` }} />
    </div>
  );
}

/** Inel de progres (SVG) */
export function ProgressRing({ value = 0, size = 44, stroke = 4, className = "" }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - stroke) / 2; const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={`shrink-0 ${className}`} role="img" aria-label={`Progres ${v}%`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--cp-neutral-soft)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--cp-accent)" strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${(v / 100) * c} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: "stroke-dasharray 600ms var(--cp-ease)" }} />
    </svg>
  );
}

/** Rând de date "cheie · valoare" (definition list) */
export function DataRow({ label, children, className = "" }) {
  return (
    <div className={`flex items-baseline justify-between gap-4 py-2.5 border-b border-cp-border last:border-0 ${className}`}>
      <dt className="cp-meta shrink-0">{label}</dt>
      <dd className="text-[13.5px] text-cp-text text-right min-w-0">{children}</dd>
    </div>
  );
}
